import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './StockManagement.css'; // Import specific CSS for this module
import { FaPlus, FaMinus, FaHistory, FaBox, FaSearch, FaChevronDown } from 'react-icons/fa';
import { productsApi, stockApi } from '../services/api';

const StockManagement = () => {
    const [products, setProducts] = useState([]);
    const [selectedProductId, setSelectedProductId] = useState('');
    const [amount, setAmount] = useState(1);
    const [note, setNote] = useState('');
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const searchInputRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch products when component mounts
    useEffect(() => {
        fetchProducts();
    }, []);

    // Fetch products from API
    const fetchProducts = async () => {
        try {
            setLoading(true);
            const response = await productsApi.getAll();
            console.log('Raw API response:', response);
            
            const formattedProducts = Array.isArray(response.data.data.products) 
                ? response.data.data.products.map(product => {
                    // Handle image URL
                    let imageUrl = product.imageUrl;
                    if (imageUrl) {
                        // If it's a relative path, prepend the API URL
                        if (!imageUrl.startsWith('http')) {
                            // Remove any leading slashes to avoid double slashes
                            const cleanPath = imageUrl.replace(/^\/+/, '');
                            imageUrl = `${process.env.REACT_APP_API_URL || 'https://igqds3armu.us-east-1.awsapprunner.com'}/${cleanPath}`;
                        }
                        console.log('Constructed image URL:', imageUrl);
                    }

                    return {
                        ...product,
                        price: parseFloat(product.price || 0),
                        stock: parseInt(product.stock || 0, 10),
                        imageUrl: imageUrl
                    };
                })
                : [];
            
            console.log('Formatted products:', formattedProducts);
            setProducts(formattedProducts);
            setError(null);
        } catch (err) {
            console.error('Error fetching products:', err);
            setError('Failed to fetch products. Please try again later.');
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch stock history (prefer StockTransactionHistories, fallback to stock_movements)
    const fetchHistory = useCallback(async () => {
        try {
            const params = {};
            if (selectedProductId) params.productId = selectedProductId;
            const resHist = await stockApi.getHistory(params);
            let historyData = resHist?.data?.data?.history || [];

            // Fallback: if history empty, use movements
            if (!Array.isArray(historyData) || historyData.length === 0) {
                const resMov = await stockApi.getAll();
                const tx = resMov?.data?.data?.transactions || [];
                historyData = tx;
            }

            setHistory(Array.isArray(historyData) ? historyData : []);
        } catch (err) {
            console.error('Error fetching stock history:', err);
            setHistory([]);
        }
    }, [selectedProductId]);

    // Fetch history when mount and when selected product changes
    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    // Filter products based on search term
    const filteredProducts = useMemo(() => {
        return Array.isArray(products) ? products.filter(product => {
            return product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   (product.description || '').toLowerCase().includes(searchTerm.toLowerCase());
        }) : [];
    }, [products, searchTerm]);

    // Get the selected product
    const selectedProduct = products.find(p => p.id === selectedProductId) || null;
    
    // (no direct client-side stock update; backend handles stock change)

    const increaseStock = async () => {
        if (!selectedProductId) {
            alert("Please select a product first!");
            return;
        }
        
        try {
            const product = products.find(p => p.id === selectedProductId);
            console.log('Current product:', product);
            // Create the stock transaction; backend will update stock and create history
            const stockTransaction = {
                productId: selectedProductId,
                type: 'in',
                quantity: parseInt(amount, 10),
                reason: 'Manual stock increase',
                transactionDate: new Date().toISOString(),
                unitPrice: product.price || 0, // Add unit price from product
                totalAmount: (product.price || 0) * parseInt(amount, 10), // Calculate total amount
                notes: note?.trim() || undefined
            };
            
            console.log('Creating stock transaction:', stockTransaction);
            await stockApi.create(stockTransaction);
            
            await fetchProducts();
            await fetchHistory(); // Refresh history
            setNote('');
            setError(null); // Clear any previous errors
        } catch (err) {
            console.error('Error increasing stock:', {
                error: err,
                response: err.response?.data,
                status: err.response?.status
            });
            setError(`Failed to increase stock: ${err.response?.data?.message || err.message}`);
        }
    };

    const decreaseStock = async () => {
        if (!selectedProductId) {
            alert("Please select a product first!");
            return;
        }
        
        try {
            const product = products.find(p => p.id === selectedProductId);
            // Create the stock transaction with the exact format required by the backend
            const stockTransaction = {
                productId: parseInt(selectedProductId, 10), // Ensure productId is an integer
                type: 'out', // Changed to lowercase to match database schema
                quantity: parseInt(amount, 10), // Ensure quantity is an integer
                reason: 'Manual stock decrease',
                transactionDate: new Date().toISOString(),
                unitPrice: product.price || 0, // Add unit price from product
                totalAmount: (product.price || 0) * parseInt(amount, 10), // Calculate total amount
                notes: note?.trim() || undefined
            };
            
            console.log('Creating stock transaction:', stockTransaction);
            
            await stockApi.create(stockTransaction);
            
            // Refresh both products and history
            await fetchProducts();
            await fetchHistory();
            setNote('');
            setError(null); // Clear any previous errors
        } catch (err) {
            console.error('Error decreasing stock:', {
                error: err,
                response: err.response?.data,
                status: err.response?.status
            });
            
            // Format the error message
            let errorMessage = 'Failed to decrease stock';
            if (err.response?.data?.errors) {
                errorMessage = err.response.data.errors.map(e => `${e.field}: ${e.message}`).join(', ');
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            }
            
            setError(errorMessage);
        }
    };

    // Helper function to format image URLs - removed as unused

    // Image error handler with better error handling
    // const handleImageError = (e) => {
    //     const img = e.currentTarget;
    //     const defaultPlaceholder = 'https://placehold.co/400x400?text=No+Image';
    //     if (!img.src.includes('placehold.co')) {
    //         img.src = defaultPlaceholder;
    //     }
    // };

    // Use this component for product images
    // const ProductImage = ({ src, alt, className }) => {
    //     const imageUrl = formatImageUrl(src);
    //     const isBackendUrl = imageUrl.includes(process.env.REACT_APP_API_URL) || imageUrl.includes('igqds3armu.us-east-1.awsapprunner.com');
    //     
    //     return (
    //         <img
    //             src={imageUrl}
    //             alt={alt || 'Product'}
    //             className={`${className || ''} ${!src ? 'image-load-failed' : ''}`}
    //             onError={handleImageError}
    //             loading="lazy"
    //             crossOrigin={isBackendUrl ? "use-credentials" : "anonymous"}
    //             referrerPolicy="no-referrer"
    //             style={{ imageRendering: 'auto' }}
    //         />
    //     );
    // };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!isDropdownOpen) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                setIsDropdownOpen(true);
                setHighlightedIndex(0);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev < filteredProducts.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
                    setSelectedProductId(filteredProducts[highlightedIndex].id);
                    setIsDropdownOpen(false);
                    setHighlightedIndex(-1);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsDropdownOpen(false);
                setHighlightedIndex(-1);
                break;
            case 'Tab':
                setIsDropdownOpen(false);
                setHighlightedIndex(-1);
                break;
            default:
                if (isDropdownOpen) {
                    searchInputRef.current?.focus();
                }
                break;
        }
    };

    // Reset highlight when filtered products change
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [filteredProducts]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isDropdownOpen) {
            searchInputRef.current?.focus();
        }
    }, [isDropdownOpen]);

    if (loading) {
        return <div className="loading">Loading stock management...</div>;
    }

    return (
        <div className="stock-management-container">
            <h1>Stock Management</h1>
            
            {error && <div className="error-message">{error}</div>}
            
            <div className="stock-dashboard">
                <div className="stock-card">
                    <h2><FaBox className="icon-space" /> Product Inventory</h2>
                    
                    <div className="product-selector">
                        <label htmlFor="product-dropdown">Select Product:</label>
                        <div 
                            className="searchable-dropdown" 
                            ref={dropdownRef}
                            onKeyDown={handleKeyDown}
                            role="combobox"
                            aria-expanded={isDropdownOpen}
                            aria-haspopup="listbox"
                            aria-controls="product-list"
                        >
                            <div 
                                id="product-dropdown"
                                className="dropdown-header" 
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                tabIndex={0}
                                role="button"
                                aria-label="Toggle product dropdown"
                            >
                                <div className="selected-product">
                                    {selectedProduct ? (
                                        <span>{selectedProduct.name} (Stock: {selectedProduct.stock})</span>
                                    ) : (
                                        <span className="placeholder">Search or select a product...</span>
                                    )}
                                </div>
                                <FaChevronDown className={`dropdown-icon ${isDropdownOpen ? 'open' : ''}`} aria-hidden="true" />
                            </div>
                            
                            {isDropdownOpen && (
                                <div className="dropdown-content">
                                    <div className="search-box">
                                        <FaSearch className="search-icon" aria-hidden="true" />
                                        <input 
                                            ref={searchInputRef}
                                            type="text" 
                                            placeholder="Search products..." 
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setHighlightedIndex(0);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            aria-label="Search products"
                                            aria-controls="product-list"
                                        />
                                    </div>
                                    
                                    <div 
                                        id="product-list"
                                        className="product-list"
                                        role="listbox"
                                        aria-label="Product list"
                                    >
                                        {filteredProducts.length > 0 ? (
                                            filteredProducts.map((product, index) => (
                                                <div 
                                                    key={product.id} 
                                                    className={`product-option ${selectedProductId === product.id ? 'selected' : ''} ${index === highlightedIndex ? 'highlighted' : ''}`}
                                                    onClick={() => {
                                                        setSelectedProductId(product.id);
                                                        setIsDropdownOpen(false);
                                                        setHighlightedIndex(-1);
                                                    }}
                                                    onMouseEnter={() => setHighlightedIndex(index)}
                                                    role="option"
                                                    aria-selected={selectedProductId === product.id}
                                                >
                                                    <span className="product-name">{product.name}</span>
                                                    <span className="product-stock">Stock: {product.stock}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="no-results" role="status">
                                                No products found matching "{searchTerm}"
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {selectedProduct && (
                        <div className="selected-product-info">
                            <h3>{selectedProduct.name}</h3>
                            <div className="stock-value">{selectedProduct.stock}</div>
                            
                            <div className="stock-controls">
                                <div className="amount-control">
                                    <label htmlFor="amount">Amount:</label>
                                    <input 
                                        id="amount"
                                        type="number" 
                                        min="1" 
                                        value={amount} 
                                        onChange={(e) => setAmount(parseInt(e.target.value) || 1)} 
                                    />
                                </div>
                                <div className="note-control">
                                    <label htmlFor="note">Note:</label>
                                    <input
                                        id="note"
                                        type="text"
                                        placeholder="Optional note for this movement"
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                    />
                                </div>
                                
                                <div className="stock-actions">
                                    <button className="btn-increase" onClick={increaseStock}>
                                        <FaPlus /> Add Stock
                                    </button>
                                    <button className="btn-decrease" onClick={decreaseStock}>
                                        <FaMinus /> Remove Stock
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {!selectedProduct && (
                        <div className="no-product-selected">
                            <p>Please select a product to manage its inventory</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="history-section">
                <div className="history-header">
                    <h3><FaHistory /> Stock History</h3>
                </div>
                
                {Array.isArray(history) && history.length > 0 ? (
                    <div className="history-table-container">
                        <table className="history-table">
                            <thead>
                                <tr>
                                    <th>Date & Time</th>
                                    <th>Product</th>
                                    <th>Type</th>
                                    <th>Quantity Δ</th>
                                    <th>Prev → New</th>
                                    <th>Reason</th>
                                    <th>User</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((h) => {
                                    const createdAt = h.created_at || h.createdAt;
                                    const productName = h.Product?.name || '-';
                                    const userName = h.User?.name || '-';
                                    const type = h.type || '-';
                                    const prev = h.previousQuantity ?? '-';
                                    const next = h.newQuantity ?? '-';
                                    const quantityDelta =
                                        h.newQuantity !== undefined && h.previousQuantity !== undefined
                                            ? (parseInt(h.newQuantity, 10) - parseInt(h.previousQuantity, 10))
                                            : '-';
                                    const reason = h.reason ?? '';
                                    return (
                                        <tr key={h.id} className={type === 'in' ? 'increase-row' : 'decrease-row'}>
                                            <td>{createdAt ? new Date(createdAt).toLocaleString() : '-'}</td>
                                            <td>{productName}</td>
                                            <td>{type}</td>
                                            <td>{quantityDelta}</td>
                                            <td>{prev} → {next}</td>
                                            <td>{reason}</td>
                                            <td>{userName}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="no-history">
                        <p>No stock movements recorded yet.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StockManagement; 